import React, { Dispatch, SetStateAction, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import clsx from 'clsx';
import { useOnClickOutside } from 'usehooks-ts';

import { IconButton, Label, LABEL_TYPE } from '@/components/generic';
import { IconButtonVariant } from '@/components/generic/IconButton/icon-button-config';
import TimeInput from '@/components/generic/TimeInput/TimeInput';
import { AUDIO, IMAGE, TEXT_OVERLAY } from '@/constants';
import { AssetType } from '@/enums';
import { formatDuration } from '@/helpers';
import { RemoveSpecialCharacters } from '@/helpers/NamingHelper';
import { useMobileScreen } from '@/hooks';
import { ClockIcon, DeleteIcon, DragIndicator } from '@/resources';
import musicThumbnail from '@/resources/img/music-thumbnail.png';
import { Asset, PlaylistAsset } from '@/types';

interface PlaylistAssetItemProps {
  playlistAsset: PlaylistAsset;
  index: number;
  onUpdate: (from: number, to: number) => void;
  onRemove: (id: number) => void;
  onEditDuration: (id: number, newDuration: number) => void;
  showDragIconIndex: boolean;
  setShowDragIconIndex: Dispatch<SetStateAction<number | null>>;
}

export const PlaylistAssetItem = ({
  playlistAsset,
  index,
  onUpdate,
  onRemove,
  onEditDuration,
  showDragIconIndex,
  setShowDragIconIndex
}: PlaylistAssetItemProps) => {
  const [t] = useTranslation('common');
  const isMobileView = useMobileScreen();
  const [isDragged, setIsDragged] = useState(false);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [showDragIcon, setShowDragIcon] = useState(false);
  const [editingDuration, setEditingDuration] = useState(false);
  const defaultValue = 15;
  const [tempDuration, setTempDuration] = useState<number>(playlistAsset.duration || defaultValue);
  const [touchStartPos, setTouchStartPos] = useState<{ x: number; y: number } | null>(null);
  const [touchDragIndex, setTouchDragIndex] = useState<number | null>(null);
  const isImageOrTextOverlay =
    playlistAsset.asset.assetType === IMAGE || playlistAsset.asset.assetType === TEXT_OVERLAY;

  const ref = useRef<HTMLDivElement>(null);
  const durationPopupRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(durationPopupRef, () => {
    setEditingDuration(false);
    if (playlistAsset.id !== undefined && playlistAsset.id !== null) {
      onEditDuration(playlistAsset.id, tempDuration);
    }
  });

  const handleMouseEnter = useCallback(() => {
    setShowDragIcon(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setShowDragIcon(false);
  }, []);

  const handleTouchStartMemo = useCallback(() => {
    setShowDragIcon(true);
    setShowDragIconIndex(index);
  }, [index, setShowDragIconIndex]);

  const handleTouchCancel = useCallback(() => {
    setShowDragIcon(false);
    setShowDragIconIndex(null);
  }, [setShowDragIconIndex]);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    setIsDragged(true);
    e.dataTransfer.setData('fromIndex', String(index));
  };
  const handleDragEnd = () => {
    setIsDragged(false);
    setDragOffset({ x: 0, y: 0 });
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    setIsDragged(false);
    setDragOffset({ x: 0, y: 0 });
    if (e.dataTransfer.getData('fromIndex')) {
      const from = parseInt(e.dataTransfer.getData('fromIndex'));
      if (from !== index) {
        onUpdate(from, index);
      }
    }
  };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const touch = e.touches[0];
    setTouchStartPos({ x: touch.clientX, y: touch.clientY });
    setTouchDragIndex(index);
    setIsDragged(false);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartPos && touchDragIndex !== null) {
      const touch = e.touches[0];
      const deltaX = touch.clientX - touchStartPos.x;
      const deltaY = touch.clientY - touchStartPos.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      if (distance > 10 && !isDragged) {
        setIsDragged(true);
      }
      if (isDragged) {
        setDragOffset({ x: deltaX, y: deltaY });
        e.preventDefault();
        e.stopPropagation();
      }
    }
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (isDragged && touchDragIndex !== null) {
      const touch = e.changedTouches[0];
      const actualX = touch.clientX;
      const actualY = touch.clientY;
      const draggedElement = ref.current;
      if (draggedElement) {
        const originalPointerEvents = draggedElement.style.pointerEvents;
        draggedElement.style.pointerEvents = 'none';
        const elementBelow = document.elementFromPoint(actualX, actualY);
        draggedElement.style.pointerEvents = originalPointerEvents;
        let targetElement = elementBelow;
        let dropIndex = -1;
        while (targetElement && targetElement !== document.body) {
          if (targetElement.getAttribute && targetElement.getAttribute('data-playlist-index')) {
            const attr = targetElement.getAttribute('data-playlist-index');
            if (attr) {
              dropIndex = parseInt(attr);
            }
            break;
          }
          targetElement = targetElement.parentElement;
        }
        if (dropIndex !== -1 && dropIndex !== touchDragIndex) {
          onUpdate(touchDragIndex, dropIndex);
        }
      }
    }
    setTouchDragIndex(null);
    setTouchStartPos(null);
    setIsDragged(false);
    setDragOffset({ x: 0, y: 0 });
  };

  const handleEditDuration = () => {
    setEditingDuration(true);
    setTempDuration(playlistAsset.duration || defaultValue);
  };

  const handleThumbnail = (asset: Asset) => {
    switch (asset.assetType) {
      case AUDIO:
        return musicThumbnail;
      default:
        return asset.thumbnailUrl;
    }
  };

  return (
    <div
      ref={ref}
      className={clsx('group flex w-full cursor-pointer flex-row items-center gap-x-4', {
        'opacity-50': isDragged,
        'shadow-lg': isDragged
      })}
      style={
        isDragged
          ? {
              transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)`,
              position: 'relative',
              zIndex: 1000
            }
          : {}
      }
      draggable
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragEnd={handleDragEnd}
      onTouchStart={handleTouchStartMemo}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      data-playlist-index={index}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchCancel={handleTouchCancel}>
      <div className='relative size-24 shrink-0 sm:size-32 md:size-14 lg:size-16 xl:size-28 2xl:size-36 3xl:size-36 4xl:size-40'>
        <img
          id={playlistAsset.asset.id.toString()}
          data-type={(playlistAsset.asset as Asset).assetType}
          src={handleThumbnail(playlistAsset.asset as Asset)}
          className={clsx(
            'size-full rounded-lg',
            playlistAsset.asset.assetType === AssetType.COVER_PHOTO.toString()
              ? 'object-contain'
              : 'aspect-square object-cover'
          )}
        />
        {isMobileView
          ? showDragIconIndex && (
              <div
                className='absolute bottom-0 left-0 z-10 flex size-6 cursor-move items-center justify-center rounded-bl-lg rounded-tr-lg bg-white/80'
                onTouchStart={handleTouchStart}>
                <DragIndicator className='size-4 text-primary-black' />
              </div>
            )
          : showDragIcon && (
              <div className='absolute bottom-0 left-0 z-10 flex size-6 cursor-move items-center justify-center rounded-bl-lg rounded-tr-lg bg-white/80'>
                <DragIndicator className='size-4 text-primary-black' />
              </div>
            )}
      </div>
      <div className='flex flex-row gap-x-3 overflow-hidden'>
        <div className='flex shrink-0 flex-col md:hidden lg:gap-x-4 xl:flex xl:shrink-0 xl:flex-col 2xl:flex 2xl:shrink-0 2xl:flex-col 3xl:flex 3xl:shrink-0 3xl:flex-col 4xl:flex 4xl:shrink-0 4xl:flex-col'>
          <Label text={t('assetName') + ':'} type={LABEL_TYPE.PARAGRAPH1} className='truncate' />
          <Label text={t('assetType') + ':'} type={LABEL_TYPE.PARAGRAPH1} className='truncate' />
          <Label text={t('duration') + ':'} type={LABEL_TYPE.PARAGRAPH1} className='truncate' />
        </div>
        <div className='flex min-w-0 flex-col gap-x-4'>
          <Label
            text={RemoveSpecialCharacters(playlistAsset.asset.assetName)}
            type={LABEL_TYPE.PARAGRAPH1}
            className='truncate !text-primary-black'
          />
          <Label
            text={playlistAsset.asset.assetType}
            type={LABEL_TYPE.PARAGRAPH1}
            className='truncate !text-primary-black'
          />
          <Label
            text={formatDuration(
              isImageOrTextOverlay
                ? editingDuration
                  ? tempDuration
                  : playlistAsset.duration
                : playlistAsset.asset.duration
            )}
            type={LABEL_TYPE.PARAGRAPH1}
            className='truncate !text-primary-black'
          />
        </div>
      </div>
      <div className='relative ml-auto flex flex-row items-center gap-x-4'>
        <IconButton
          handleOnClick={handleEditDuration}
          variant={IconButtonVariant.PRIMARY}
          className={clsx({
            invisible: !isImageOrTextOverlay
          })}>
          <ClockIcon className='h-4 w-3 text-primary-blue md:h-5 md:w-4' />
        </IconButton>
        <IconButton
          handleOnClick={() => {
            if (playlistAsset.id !== undefined && playlistAsset.id !== null) {
              onRemove(playlistAsset.id);
            }
          }}
          variant={IconButtonVariant.PRIMARY}>
          <DeleteIcon className='h-4 w-3 text-primary-blue md:h-5 md:w-4' />
        </IconButton>
        {editingDuration && (
          <div
            className='absolute right-0 top-full z-50 rounded-lg shadow-lg'
            ref={durationPopupRef}>
            <TimeInput
              label={''}
              setFieldValue={(_, value) => {
                if (typeof value === 'number' && !isNaN(value) && value >= 0) {
                  setTempDuration(value);
                }
              }}
              className={'gap-2'}
              fieldName={`duration-${playlistAsset.id}`}
              defaultValue={tempDuration}
              isRequired={false}
              icon={<ClockIcon className='size-4 font-bold text-primary-black md:size-5' />}
              hasSpinners={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};
